import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CryptoJS from "crypto-js";
import HidePasswordIcon from "../components/HidePasswordIcon";
import { useSelector } from "react-redux";

/**
 * Signup est une page qui permet à l'utilisateur de créer un nouveau compte.
 * Elle contient un formulaire où l'utilisateur peut entrer son nom d'utilisateur, son mot de passe, son prénom, son nom de famille,
 * le nom de son entreprise et son numéro SIRET.
 * Lorsque le formulaire est soumis, une requête POST est envoyée à l'API pour créer le nouveau compte.
 * Si la création du compte réussit, l'utilisateur est redirigé vers la page de connexion.
 * Si la création du compte échoue, un message d'erreur est affiché.
 * Les informations de l'entreprise et du SIRET peuvent être pré-remplies à partir de l'URL si elles sont fournies sous forme cryptée.
 * Cette page utilise la bibliothèque CryptoJS pour le chiffrement AES.
 * Cette page utilise le hook useState de React pour gérer l'état des champs de saisie et de la modale.
 * Cette page utilise le hook useEffect de React pour décrypter les informations de l'entreprise et du SIRET à partir de l'URL.
 * Cette page utilise le hook useParams de 'react-router-dom' pour obtenir les informations de l'entreprise et du SIRET à partir de l'URL.
 * Cette page utilise le composant Modal pour afficher un message de confirmation après la création du compte.
 *
 */

const apiPrefix = process.env.REACT_APP_API_PREFIX;
const COMPANY_SIRET_REQUIRED_MESSAGE =
  "Le nom de société et le SIRET sont obligatoires pour l'inscription.";

const readSignupTokenPayload = (signupToken) => {
  try {
    const payload = String(signupToken).split(".")[0];
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    const binary = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const parsed = JSON.parse(new TextDecoder().decode(bytes));
    return {
      company: String(parsed.company || "").trim(),
      siret: String(parsed.siret || "").trim(),
    };
  } catch {
    return null;
  }
};

const Signup = () => {
  const Numberapp = useSelector((state) => state.app.appNumber);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");

  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [company, setCompany] = useState("");
  const [siret, setSiret] = useState("");
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [inviteError, setInviteError] = useState(COMPANY_SIRET_REQUIRED_MESSAGE);
  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const regexEmail = /^[^@\s]{2,30}@[^\s@]{2,30}\.[^@\s]{2,}$/;
  const regexPassword =
    /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*+.~/-]).{8,}$/;
  const forbiddenEmailPatterns = ["intradef.gouv.fr"];

  const containsForbiddenPattern = (email, appNumber) => {
    if (appNumber !== 5) return false;
    const lowerEmail = email.toLowerCase();
    return forbiddenEmailPatterns.some((pattern) =>
      lowerEmail.includes(pattern.toLowerCase())
    );
  };

  const { encryptedEntreprise, encryptedSiret, signupToken } = useParams();

  useEffect(() => {
    let nextCompany = "";
    let nextSiret = "";

    if (signupToken) {
      const linked = readSignupTokenPayload(signupToken);
      nextCompany = linked?.company || "";
      nextSiret = linked?.siret || "";
      setCompany(nextCompany);
      setSiret(nextSiret);
      setInviteError(
        nextCompany && nextSiret ? null : "Lien d'inscription invalide."
      );
      return;
    }

    if (encryptedEntreprise && encryptedSiret) {
      try {
        const urlSafeEntreprise = decodeURIComponent(encryptedEntreprise);
        const urlSafeSiret = decodeURIComponent(encryptedSiret);

        const bytesEntreprise = CryptoJS.AES.decrypt(
          urlSafeEntreprise,
          "secret key 123"
        );
        nextCompany = bytesEntreprise.toString(CryptoJS.enc.Utf8);
        // console.log('Entreprise décryptée:', originalEntreprise);

        const bytesSiret = CryptoJS.AES.decrypt(urlSafeSiret, "secret key 123");
        nextSiret = bytesSiret.toString(CryptoJS.enc.Utf8);
        // console.log('SIRET décrypté:', originalSiret);
      } catch (decryptError) {
        console.error("Erreur de décryptage:", decryptError);
      }
    }

    setCompany(nextCompany);
    setSiret(nextSiret);

    if (!nextCompany.trim() || !nextSiret.trim()) {
      setInviteError(COMPANY_SIRET_REQUIRED_MESSAGE);
    } else {
      setInviteError(null);
    }
  }, [encryptedEntreprise, encryptedSiret, signupToken]);

  const hide = () => {
    setShowModal(false);
    navigate("/signin");
  };

  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    if (error) setError(null);
    if (containsForbiddenPattern(e.target.value, Numberapp)) {
      setUsernameError(
        "Cette adresse e-mail n'est pas autorisée pour l'inscription sur cette plateforme."
      );
    } else if (!regexEmail.test(e.target.value)) {
      setUsernameError(
        "Le nom d'utilisateur doit être une adresse e-mail valide."
      );
    } else {
      setUsernameError("");
    }
  };
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    // Réinitialiser l'erreur générale si elle existe
    if (error) setError(null);
    if (!regexPassword.test(e.target.value)) {
      setPasswordError(
        "Le mot de passe doit contenir au moins 8 caractères, dont une majuscule, une minuscule, un chiffre et un caractère spécial (par exemple : !, @, #, $, %, &, , ?, -, _, + ou =)."
      );
    } else {
      setPasswordError("");
    }
    if (confirmPassword && e.target.value !== confirmPassword) {
      setConfirmPasswordError("Les mots de passe ne correspondent pas.");
    } else {
      setConfirmPasswordError("");
    }
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    // Réinitialiser l'erreur générale si elle existe
    if (error) setError(null);
    if (password && e.target.value !== password) {
      setConfirmPasswordError("Les mots de passe ne correspondent pas.");
    } else {
      setConfirmPasswordError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Réinitialiser les erreurs précédentes
    setError(null);
    let hasErrors = false;

    if (containsForbiddenPattern(username, Numberapp)) {
      setUsernameError(
        "Cette adresse e-mail n'est pas autorisée pour l'inscription sur cette plateforme."
      );
      setError("Veuillez corriger les erreurs dans le formulaire.");
      hasErrors = true;
    } else if (!regexEmail.test(username)) {
      setUsernameError(
        "Le nom d'utilisateur doit être une adresse e-mail valide."
      );
      setError("Veuillez corriger les erreurs dans le formulaire.");
      hasErrors = true;
    }
    if (!regexPassword.test(password)) {
      setPasswordError(
        "Le mot de passe doit contenir au moins 8 caractères, dont une majuscule, une minuscule, un chiffre et un caractère spécial (par exemple : !, @, #, $, %, &, , ?, -, _, + ou =)."
      );
      setError("Veuillez corriger les erreurs dans le formulaire.");
      hasErrors = true;
    }
    if (password !== confirmPassword) {
      setConfirmPasswordError("Les mots de passe ne correspondent pas.");
      setError("Veuillez corriger les erreurs dans le formulaire.");
      hasErrors = true;
    }

    if (!company.trim() || !siret.trim()) {
      setInviteError(COMPANY_SIRET_REQUIRED_MESSAGE);
      setError("Veuillez corriger les erreurs dans le formulaire.");
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    const formData = {
      username: username,
      password: password,
      firstname: firstname,
      lastname: lastname,
      company: company,
      siret: siret,
      appNumber: Numberapp,
      ...(signupToken ? { signupToken } : {}),
    };

    try {
      const response = await fetch(apiPrefix + "register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        //  console.log('Inscription réussie !');
        setShowModal(true); // Affichez la modale
      } else if (response.status === 409) {
        // Gérer le cas où le nom d'utilisateur existe déjà
        const data = await response.json();
        setError(data.error); // Afficher l'erreur renvoyée par le backend
      } else if (response.status === 400) {
        try {
          const data = await response.json();
          setError(data.error || COMPANY_SIRET_REQUIRED_MESSAGE);
        } catch {
          setError(COMPANY_SIRET_REQUIRED_MESSAGE);
        }
      } else {
        console.error("Erreur lors de l'inscription:", response.statusText);
      }
    } catch (error) {
      console.error("Erreur lors de la tentative d'inscription:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="card-theme">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-theme-primary mb-6">
              Inscription
            </h2>
            {inviteError && (
              <div className="bg-red-50 border-2 border-red-400 text-red-800 px-4 py-3 rounded-lg mb-4 flex items-center shadow-md">
                <i className="fas fa-exclamation-circle mr-2 text-red-600 text-lg"></i>
                <span className="font-semibold">{inviteError}</span>
              </div>
            )}
            {error && (
              <div className="bg-red-50 border-2 border-red-400 text-red-800 px-4 py-3 rounded-lg mb-4 flex items-center shadow-md">
                <i className="fas fa-exclamation-circle mr-2 text-red-600 text-lg"></i>
                <span className="font-semibold">{error}</span>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                <i className="fas fa-envelope mr-2 text-theme-accent"></i>
                Votre adresse Email :
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={handleUsernameChange}
                className={`input-theme ${
                  usernameError
                    ? "border-2 border-red-500 focus:border-red-500 focus:ring-red-500"
                    : ""
                }`}
                placeholder="votre@email.com"
                required
              />
              {usernameError && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3 mt-2 rounded flex items-start">
                  <i className="fas fa-exclamation-triangle mr-2 mt-0.5 text-red-600"></i>
                  <span className="text-sm font-medium">{usernameError}</span>
                </div>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                <i className="fas fa-key mr-2 text-theme-accent"></i>
                Choisir votre Mot de passe :
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={handlePasswordChange}
                  className={`input-theme pr-12 ${
                    passwordError
                      ? "border-2 border-red-500 focus:border-red-500 focus:ring-red-500"
                      : ""
                  }`}
                  placeholder="Votre mot de passe"
                  required
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <HidePasswordIcon
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                  />
                </div>
              </div>
              {passwordError && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3 mt-2 rounded flex items-start">
                  <i className="fas fa-exclamation-triangle mr-2 mt-0.5 text-red-600"></i>
                  <span className="text-sm font-medium">{passwordError}</span>
                </div>
              )}
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                <i className="fas fa-key mr-2 text-theme-accent"></i>
                Confirmer le mot de passe :
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  className={`input-theme pr-12 ${
                    confirmPasswordError
                      ? "border-2 border-red-500 focus:border-red-500 focus:ring-red-500"
                      : ""
                  }`}
                  placeholder="Confirmez votre mot de passe"
                  required
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <HidePasswordIcon
                    showPassword={showConfirmPassword}
                    setShowPassword={setShowConfirmPassword}
                  />
                </div>
              </div>
              {confirmPasswordError && (
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3 mt-2 rounded flex items-start">
                  <i className="fas fa-exclamation-triangle mr-2 mt-0.5 text-red-600"></i>
                  <span className="text-sm font-medium">
                    {confirmPasswordError}
                  </span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="firstname"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  <i className="fas fa-user mr-2 text-theme-accent"></i>
                  Prénom :
                </label>
                <input
                  type="text"
                  id="firstname"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  className="input-theme"
                  placeholder="Votre prénom"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="lastname"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  <i className="fas fa-user mr-2 text-theme-accent"></i>
                  Nom de famille :
                </label>
                <input
                  type="text"
                  id="lastname"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  className="input-theme"
                  placeholder="Votre nom"
                  required
                />
              </div>
            </div>

            {/* Champs cachés */}
            <input type="hidden" id="company" value={company} />
            <input type="hidden" id="siret" value={siret} />

            <button
              type="submit"
              className="btn-theme w-full disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!company.trim() || !siret.trim() || Boolean(inviteError)}
            >
              S'inscrire
            </button>
          </form>
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-8 relative transform transition-all scale-100">
              <button
                onClick={hide}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-3xl font-bold transition-colors"
                aria-label="Fermer"
              >
                &times;
              </button>
              <div className="text-center">
                <div className="mb-4">
                  <i className="fas fa-check-circle text-green-500 text-6xl mb-4"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Inscription réussie !
                </h2>
                <p className="text-gray-600 mb-6">
                  Votre compte a été créé avec succès. Vous allez être redirigé
                  vers la page de connexion.
                </p>
                <button onClick={hide} className="btn-theme w-full">
                  Continuer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Signup;
